import {
    SubscriptionStatus,
    type Subscription,
    type SubscriptionLine,
    type SubscriptionProductPrice,
} from '@generated/graphql';
import { formatCurrencyWithLocale } from '@utils/math';

export function isString(value: unknown): value is string {
    return typeof value === 'string';
}

export const normalizeConnectorIds = (connectors?: (string | null)[] | null): string[] =>
    (connectors ?? []).filter(isString);

export const parseDate = (value?: string | null): Date | undefined =>
    value ? new Date(value) : undefined;

export const formatCurrency = (value: number, currency?: string): string =>
    currency ? formatCurrencyWithLocale(value, navigator.language, currency) : 'N/A';

export const isPriceApplicableForDate = (
    price?: SubscriptionProductPrice | null,
    date?: Date | null
): boolean => {
    if (!price || !date) {
        return true;
    }

    const validFrom = parseDate(price.validFrom);
    const validTo = parseDate(price.validTo);

    if (validFrom && date < validFrom) {
        return false;
    }

    if (validTo && date > validTo) {
        return false;
    }

    return true;
};

export const sortLinesByValidFrom = (lines: SubscriptionLine[]): SubscriptionLine[] =>
    [...lines].sort((a, b) => {
        const aFrom =
            parseDate(a.product?.productPrice?.validFrom)?.getTime() ?? Number.NEGATIVE_INFINITY;
        const bFrom =
            parseDate(b.product?.productPrice?.validFrom)?.getTime() ?? Number.NEGATIVE_INFINITY;
        return aFrom - bFrom;
    });

export const pickLineForDate = (
    lines: (SubscriptionLine | null | undefined)[] | null | undefined,
    matchesLine: (line: SubscriptionLine) => boolean,
    date: Date
): SubscriptionLine | undefined => {
    if (!lines?.length) {
        return undefined;
    }

    const matchingLines = lines.filter((line): line is SubscriptionLine =>
        Boolean(line && matchesLine(line))
    );

    if (!matchingLines.length) {
        return undefined;
    }

    const applicable = matchingLines.find((line) =>
        isPriceApplicableForDate(line.product?.productPrice, date)
    );
    if (applicable) {
        return applicable;
    }

    const sortedByValidFrom = sortLinesByValidFrom(matchingLines);

    const pastLines = sortedByValidFrom.filter((line) => {
        const validFrom = parseDate(line.product?.productPrice?.validFrom);
        return validFrom ? validFrom.getTime() <= date.getTime() : true;
    });

    if (pastLines.length) {
        return pastLines[pastLines.length - 1];
    }

    return sortedByValidFrom[0];
};

export const selectBaselineLine = (
    subscriptionLines: (SubscriptionLine | null | undefined)[] | null | undefined,
    calculationDate: Date
): SubscriptionLine | undefined =>
    pickLineForDate(
        subscriptionLines,
        (line) => line.product?.baseSubscriptionProduct ?? true,
        calculationDate
    );

export const deriveCalculationDate = (subscription?: Subscription | null): Date => {
    if (subscription?.nextInvoiceDate) {
        return new Date(subscription.nextInvoiceDate);
    }

    if (subscription?.status === SubscriptionStatus.Draft) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 1);
    }

    return new Date();
};
